/**
 * Piezo field registry — single source of truth mapping a GraphQL field path to the migrated tool
 * (and exact parameters) it invokes. piezoLink walks an operation's selection set and, for every
 * selected field present here, emits the descriptor(s) to sign. This replaces error-prone per-hook
 * `context.piezoToolCalls` wiring: any query selecting these fields is auto-covered, so we can't
 * "forget" to sign a tool.
 *
 * Parameters MUST match what the server dataSource method sends: the server builds its own arguments, and
 * Mechanic rejects the call if their canonical (JCS) hash differs from the signed one.
 *
 * Absent because their tools are not migrated, so they must NOT be signed: sop, tms/tickets, citadel,
 * sim, insights.
 */
import { DocumentNode, OperationDefinitionNode, SelectionSetNode } from 'graphql';
import { PiezoToolCall } from './toolCalls';

type Vars = Record<string, any>;
type Descriptor = PiezoToolCall | PiezoToolCall[];
type Builder = (v: Vars) => Descriptor | undefined;

const CURL = 'aes/domain/dp/node/es/curl/get';
const CONFIG = 'aes/domain/cp/get-elasticsearch-domain-config';

/** Domain-scoped tool with only { domain_identifier }. */
const dom = (key: string, toolId: string): Builder => (v) => ({
  key,
  toolId,
  parameters: { domain_identifier: v.domainIdentifier },
});
/**
 * The url TumblerApi.getCurl sends. Must match its construction byte for byte: the signature covers the
 * exact string, and a mismatch is rejected by piezod as an empty result rather than an error.
 */
export const buildCurlUrl = (apiName: string, queryParams = ''): string =>
  `${apiName}?format=json&${queryParams}`;

const curl = (key: string, apiName: string, queryParams = ''): Builder => (v) => ({
  key,
  toolId: CURL,
  parameters: {
    domain_identifier: v.domainIdentifier,
    url: buildCurlUrl(apiName, queryParams),
    instance_id: v.instanceId,
  },
});

const FIELD_TOOL_MAP: Record<string, Builder> = {
  // ---- top-level ----
  // Generic Command/Node execute: the tool path is the operation's `url` variable and the exact
  // parameters are its `args`. piezoLink filters on isToolPiezoRequired, so an arbitrary url emitted here
  // is signed only if Piezo actually requires it.
  'executeCommand': (v) => ({
    key: 'executeCommand',
    toolId: v.url,
    parameters: v.args ?? {},
  }),
  'executeGetAPI': (v) => ({
    key: 'executeGetApi',
    toolId: 'aes/domain/dp/node/es/curl/get',
    parameters: {
      domain_identifier: v.domainIdentifier,
      url: `${v.url}?format=json&${v.queryParams ?? ''}`,
      instance_id: v.instanceId,
    },
  }),
  'progress': (v) => ({
    key: 'bgProgress',
    toolId: 'aes/domain/cp/describe-domain-change-progress',
    parameters: v.changeId
      ? { domain_identifier: v.domainId, change_id: v.changeId }
      : { domain_identifier: v.domainId },
  }),
  'estimate': (v) => ({
    key: 'bgEstimate',
    toolId: 'aes/domain/bg-completion-time-estimate',
    parameters: { domain_identifier: v.domainId },
  }),
  'storeLogRedactionFeedback': (v) => ({
    key: 'logRedactionFeedback',
    toolId: 'aes/domain/dp/log-redaction-feedback',
    parameters: { domain_identifier: v.domainId, ticket: v.ticket, description: v.description, breakglass_justification: v.breakglassJustification },
  }),
  'fetchEmrJobDashboard': (v) => ({
    key: 'emrJobDashboard',
    toolId: 'aes/flint/fetch-emr-job-dashboard',
    parameters: { app_id: v.appId, flint_cell: v.flintCell, job_id: v.jobId },
  }),
  'executeGetFilteredDataAPI': (v) => ({
    key: 'filtered',
    toolId: 'aes/domain/dp/node/es/curl/get-filtered-response',
    parameters: { domain_identifier: v.domainIdentifier, url: v.url, instance_id: v.instanceId },
  }),
  'account.domains': (v) => ({
    key: 'getDomains',
    toolId: 'aes/domain/cp/describe-elasticsearch-domain',
    parameters: { client_id: v.clientId },
  }),

  // ---- ClusterInsights health summary (top-level) ----
  // No instanceId variable on these two. Change them with the insights resolvers, never alone.
  'clusterHealth': (v) => ({
    key: 'insightsClusterHealth',
    toolId: CURL,
    parameters: {
      domain_identifier: v.domainIdentifier,
      url: buildCurlUrl('_cluster/health'),
      instance_id: v.instanceId,
    },
  }),
  'clusterStats': (v) => ({
    key: 'insightsClusterStats',
    toolId: CURL,
    parameters: {
      domain_identifier: v.domainIdentifier,
      url: buildCurlUrl('_cluster/stats'),
      instance_id: v.instanceId,
    },
  }),
  'numberOfNodes': (v) => ({
    key: 'numberOfNodesConfig',
    toolId: CONFIG,
    parameters: { domain_identifier: v.domainIdentifier },
  }),

  // ---- Domain control-plane fields ----
  'domain.esVersion': dom('esVersion', 'aes/domain/cp/get-elasticsearch-version'),
  'domain.cellId': dom('cellId', 'aes/domain/get-cell-details'),
  'domain.IsDomainIsolated': dom('IsDomainIsolated', 'aes/domain/cp/cs-adms-isolate'),
  'domain.DomainCreateDateTime': dom('DomainCreateDateTime', 'aes/domain/cp/get-domain-create-datetime'),
  'domain.carbonDomain': dom('carbonDomain', CONFIG),
  'domain.dragonstoneDomain': dom('dragonstoneDomain', CONFIG),
  'domain.IsRemotePublicationEnabled': dom('IsRemotePublicationEnabled', CONFIG),
  'domain.IsIndexLevelEncryptionEnabled': dom('IsIndexLevelEncryptionEnabled', CONFIG),
  'domain.DeploymentStrategy': dom('DeploymentStrategy', CONFIG),
  'domain.UpgradeDeploymentStrategy': dom('UpgradeDeploymentStrategy', CONFIG),
  'domain.IsZoaEnabled': (v) => ({
    key: 'IsZoaEnabled',
    toolId: 'aes/domain/is-feature-available',
    parameters: { domain_identifier: v.domainIdentifier, feature_tag: 'zoa_enabled' },
  }),
  'domain.rawConfig': dom('rawConfig', CONFIG),
  // Both resolve CONFIG through a helper (getEngineMode / getPackageTypeAssociatedOnDomain) rather than
  // reading it directly, which is why they were missed. Unsigned, the proxy rejects the call and the
  // helper's catch reports it as 'not a Mustang domain' / no packages.
  'domain.isMustangDomain': dom('isMustangDomain', CONFIG),
  'domain.PackageTypeAssociatedOnDomain': dom('PackageTypeAssociatedOnDomain', CONFIG),
  'domain.pinnedVFIDomain': dom('pinnedVFIDomain', 'aes/domain/cp/cs-get-registered-vfis'),
  'domain.IsSensitiveDomain': dom('IsSensitiveDomain', 'aes/domain/cp/cs-manage-sensitive-customers'),
  'domain.pinnedVFIClient': (v) => ({
    key: 'pinnedVFIClient',
    toolId: 'aes/domain/cp/cs-get-registered-vfis',
    parameters: { client_id: v.clientId },
  }),
  'domain.IsSensitiveClient': (v) => ({
    key: 'IsSensitiveClient',
    toolId: 'aes/domain/cp/cs-manage-sensitive-customers',
    parameters: { client_id: v.clientId },
  }),

  // HAProxy: version uses node/shell (both n2n variants signed) + reuses the config artifact for
  // its internal get-elasticsearch-domain-config lookup; type uses two static sdpdagent/ls calls.
  'domain.HAProxyVersion': (v) => [
    { key: 'carbonDomain', toolId: CONFIG, parameters: { domain_identifier: v.domainIdentifier } },
    { key: 'haProxyVersionEnc', toolId: 'aes/domain/dp/node/shell', parameters: { domain_identifier: v.domainIdentifier, base_cmd: 'curl', args: "-k 'https://localhost/proxy/stats'" } },
    { key: 'haProxyVersionPlain', toolId: 'aes/domain/dp/node/shell', parameters: { domain_identifier: v.domainIdentifier, base_cmd: 'curl', args: 'http://localhost/proxy/stats' } },
  ],
  'domain.HAProxyType': (v) => [
    { key: 'haProxyTypeComponents', toolId: 'aes/domain/dp/node/sdpdagent/ls', parameters: { domain_identifier: v.domainIdentifier, args: '/sdpds/components' } },
    { key: 'haProxyTypeApolloEnv', toolId: 'aes/domain/dp/node/sdpdagent/ls', parameters: { domain_identifier: v.domainIdentifier, args: '/apollo/env' } },
  ],

  // ---- Domain data-plane resources ----
  'domain.resources.ALL': dom('resourcesAll', 'aes/domain/dp/get-domain-resources'),
  'domain.resources.GROUP': dom('resourcesGroup', 'aes/domain/dp/get-group-resources'),

  // ---- ES cluster curls (getClusterInfo) ----
  'domain.es60.cluster.health': curl('clusterHealth', '_cluster/health', 'level=awareness_attributes'),
  'domain.es60.cluster.state': curl('clusterState', '_cluster/state/nodes'),
  'domain.es60.cluster.settings': curl('clusterSettings', '_cluster/settings'),
  'domain.es60.cluster.stats': curl('clusterStats', '_cluster/stats', 'human'),
  'domain.es60.cluster.allocationExplain': curl('allocationExplain', '_cluster/allocation/explain', 'human'),
  'domain.es60.cluster.decommission': curl('decommission', '_cluster/decommission/awareness/zone/_status', 'human'),
  'domain.es60.cat.nodes': curl('catNodes', '_cat/nodes'),
  'domain.es60.cat.nodeattrs': curl('catNodeattrs', '_cat/nodeattrs'),
  'domain.es60.cat.shards': curl('catShards', '_cat/shards'),
  'domain.es60.cat.allocation': curl('catAllocation', '_cat/allocation'),
  // Snapshots is a two-phase flow: repositories (static) then snapshots(repo) where repo is a query
  // variable the client supplies, so the browser can pre-sign the exact _cat/snapshots/<repo> url.
  'domain.es60.cat.repositories': curl('catRepositories', '_cat/repositories'),
  'domain.es60.cat.snapshots': (v) => ({
    key: 'catSnapshots',
    toolId: CURL,
    parameters: {
      domain_identifier: v.domainIdentifier,
      url: buildCurlUrl(`_cat/snapshots/${v.repo ?? ''}`),
      instance_id: v.instanceId,
    },
  }),

  // grep_args is a fixed string the resolver hardcodes, not a variable -- it must be signed exactly as
  // sent or the parameter hash piezod recomputes will not match.
  'domain.es60.logs.esLogs': (v) => ({
    key: 'esLogs',
    toolId: 'aes/domain/dp/grep-es-log',
    parameters: { domain_identifier: v.domainIdentifier, grep_args: '-A 5 -e INFO' },
  }),

  // ---- kibana + clusterData (getClusterInfo) ----
  'domain.kibana.health': (v) => ({
    key: 'kibanaHealth',
    toolId: 'aes/domain/dp/node/kibana/status',
    parameters: { domain_identifier: v.domainIdentifier, instance_id: v.instanceId },
  }),
  'domain.clusterData.serverType': dom('serverType', 'aes/domain/dp/node/es/curl/servertype/explain'),
  'domain.clusterData.discoveryMode': dom('discoveryMode', 'aes/domain/dp/node/es/curl/discovery/explain'),

  // ---- cross-cluster connections + follower/leader stats ----
  // Keyed on the PARENT field, not its subfields: the resolver fires these tools whenever the parent is
  // resolved, so per-subfield keys left a query that skipped them calling a Piezo-required tool unsigned.
  // Parameters must match dataSource.getOutbound/getInboundConnections and getCurl exactly.
  'crossClusterConnections': (v) => {
    const calls: PiezoToolCall[] = [
      dom('outboundConnections', 'aes/domain/cp/describe-outbound-connections')(v) as PiezoToolCall,
      dom('inboundConnections', 'aes/domain/cp/describe-inbound-connections')(v) as PiezoToolCall,
    ];
    if (v.includeReplicationStats !== false) {
      calls.push(
        curl('followerStats', '_plugins/_replication/follower_stats')(v) as PiezoToolCall,
        curl('leaderStats', '_plugins/_replication/leader_stats')(v) as PiezoToolCall,
      );
    }
    return calls;
  },
};

function walk(sel: SelectionSetNode | undefined, prefix: string, v: Vars, out: Map<string, PiezoToolCall>): void {
  if (!sel) {
    return;
  }
  for (const s of sel.selections) {
    if (s.kind !== 'Field') {
      continue; // ignore fragment spreads / inline fragments (none in these operations)
    }
    const name = s.name.value;
    if (name === '__typename') {
      continue;
    }
    const path = prefix ? `${prefix}.${name}` : name;
    const builder = FIELD_TOOL_MAP[path];
    if (builder) {
      const r = builder(v);
      const arr = Array.isArray(r) ? r : r ? [r] : [];
      for (const c of arr) {
        out.set(c.key, c); // dedup by key
      }
    }
    walk(s.selectionSet, path, v, out);
  }
}

/**
 * Derives the PiezoToolCall descriptors for an operation from its selection set. Used by piezoLink
 * as the fallback when a hook did not explicitly set `context.piezoToolCalls`.
 */
export function deriveToolCallsFromOperation(
  query: DocumentNode,
  variables: Vars | undefined,
): PiezoToolCall[] {
  const op = query.definitions.find((d) => d.kind === 'OperationDefinition') as
    | OperationDefinitionNode
    | undefined;
  if (!op) {
    return [];
  }
  const v: Vars = { ...(variables || {}) };
  if (!v.clientId && typeof v.domainIdentifier === 'string') {
    v.clientId = v.domainIdentifier.split(':')[0];
  }
  const out = new Map<string, PiezoToolCall>();
  walk(op.selectionSet, '', v, out);
  return Array.from(out.values());
}
